import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { usePersonaConfig } from '@/hooks/usePersonaConfig';
import { fetchNavigation } from '@dashai/lib/navigationClient';
import type { NavigationResponse } from '@dashai/lib/navigationClient';
import { NAVIGATION_QUERY_KEY } from '@dashai/lib/navigationCache';

interface StateWithAuth {
  auth?: { user?: { uid?: string | null } | null; isImpersonating?: boolean };
}

// The dashboards and workflows the server offers this user in `workspace`,
// split by their persona. Every navigation surface reads this one query.
//
// The cache entry belongs to one identity and one workspace, and to a staff
// member's session persona answers, which the request carries. A client user's
// persona lives on the server, so a write to it refreshes this query instead.
export function useNavigation(workspace: string | null) {
  const uid = useSelector((s: StateWithAuth) => s.auth?.user?.uid ?? null);
  const isImpersonating = useSelector((s: StateWithAuth) => Boolean(s.auth?.isImpersonating));
  const { sessionPersonaConfig } = usePersonaConfig();

  return useQuery<NavigationResponse>({
    queryKey: [NAVIGATION_QUERY_KEY, uid, workspace, isImpersonating, sessionPersonaConfig],
    queryFn: () => fetchNavigation(workspace as string, sessionPersonaConfig),
    enabled: Boolean(uid && workspace),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 2,
    // While a changed persona refetches, keep showing this workspace's menus.
    // Another user's or workspace's never stand in.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === uid && previousQuery?.queryKey[2] === workspace
        ? previous
        : undefined,
  });
}
